import { Box } from "@chakra-ui/react";
import "mapbox-gl/dist/mapbox-gl.css";
import React, { useEffect, useState } from "react";
import MapComponent, { Layer, MapRef, Marker, Source } from "react-map-gl";
import { average } from "../common/utils/average";
import { envConfig } from "../config/envConfig";
import { detailViewSlice } from "../data/detailViewSlice";
import { useAppDispatch, useAppSelector } from "../data/store";
import { SamplingData } from "../types/SamplingData";
import { SamplingLocation } from "../types/SamplingLocation";
import { MapboxPopup } from "./Popups/MapboxPopup";
import { DatesForLocation } from "./Popups/DatesForLocation";
import { useMapboxComponent } from "./useMapboxComponent";

export type ImageType =
  | "circle"
  | "triangle"
  | "square"
  | "pentagon"
  | "star"
  | "hexagon";
export type QualityColor = "green" | "yellow" | "red";

interface IProps {}

export const MapboxComponent: React.FC<IProps> = () => {
  const auth = useAppSelector((state) => state.auth);
  const mapFilter = useAppSelector((state) => state.mapFilter.value);
  const projectConfig = useAppSelector((state) => state.config.value);
  const cookies = useAppSelector((state) => state.cookies);

  const dispatcher = useAppDispatch();

  const [mapRef, setMapRef] = useState<MapRef | undefined>();
  const mapRefCallback = React.useCallback((ref: MapRef) => {
    if (ref) {
      setMapRef(ref);
    }
  }, []);

  const [viewPort, setViewPort] = useState({
    zoom: 9,
    latitude: projectConfig.defaultCoordinates.lat,
    longitude: projectConfig.defaultCoordinates.lon,
  });

  // Popup
  const [showPopup, setShowPopup] = useState<{
    display: boolean;
    location: SamplingLocation | undefined;
    data: SamplingData | undefined;
  }>({
    display: false,
    location: undefined,
    data: undefined,
  });
  // Dates popup
  const [showDatesPopup, setShowDatesPopup] = useState<{
    display: boolean;
    locationId?: number;
    name?: string;
    lat?: number;
    lon?: number;
  }>({
    display: false,
    name: undefined,
    lat: undefined,
    lon: undefined,
  });

  const { markerDataState, markerLocationState } = useMapboxComponent(
    mapFilter,
    auth
  );

  useEffect(() => {
    const resizeMapbox = (event: UIEvent) => {
      console.log("resize");
      setTimeout(() => mapRef?.resize(), 1000);
    };
    window.addEventListener("resize", resizeMapbox);
    return () => window.removeEventListener("resize", resizeMapbox);
  });

  useEffect(() => {
    if (mapFilter.filteredSamplingData.length > 0) {
      const averageLon = average(
        mapFilter.filteredSamplingData.map((it) => it.coordinates.lon)
      );
      const averageLat = average(
        mapFilter.filteredSamplingData.map((it) => it.coordinates.lat)
      );
      setViewPort((currentViewPort) => ({
        ...currentViewPort,
        longitude: averageLon,
        latitude: averageLat,
      }));
    }
  }, [mapFilter, projectConfig]);

  const [sector1, setSector1] = useState(null);
  const [sector2, setSector2] = useState(null);
  const [sector3, setSector3] = useState(null);
  const signedUrl = React.useMemo(() => {
    return [
      "https://morava.dhub.arup.com/geometry/Sector1.json",
      "https://morava.dhub.arup.com/geometry/Sector2.json",
      "https://morava.dhub.arup.com/geometry/Sector3.json",
    ];
  }, []);

  useEffect(() => {
    if (!signedUrl || signedUrl.length === 0) return;
    if (!cookies.value) return;
    const loadData = async () => {
      try {
        // 2️⃣ Hacer las peticiones
        const results = await Promise.all(
          signedUrl.map((url) =>
            fetch(url, {
              method: "GET",
              credentials: "include",
            })
              .then((res) => res.json())
              .catch((err) => {
                console.error("Error cargando GeoJSON:", err);
                return null; // Evitar que Promise.all falle
              })
          )
        );

        // 3️⃣ Guardar resultados
        const [data1, data2, data3] = results;
        if (data1) setSector1(data1);
        if (data2) setSector2(data2);
        if (data3) setSector3(data3);
      } catch (err) {
        console.error("Error obteniendo cookies o cargando datos:", err);
      }
    };

    loadData();
  }, [signedUrl, cookies]);
  // }, [signedUrl, isLoading]);
  return (
    <Box
      width="100%"
      height="100%"
      id="map-container"
      position="absolute"
      top="0"
      left="0"
      zIndex="1"
    >
      <MapComponent
        mapStyle="mapbox://styles/dhub/cl2ypoavr000z14n5af53rj77"
        mapboxAccessToken={envConfig.MAPBOX_ACCESS_TOKEN}
        initialViewState={viewPort}
        onClick={(ev) => {
          setShowPopup((state) => ({ ...state, display: false }));
          setShowDatesPopup({
            display: false,
            locationId: undefined,
            name: undefined,
            lat: undefined,
            lon: undefined,
          });
        }}
        ref={mapRefCallback}
      >
        <Source id="Sector1" type="geojson" data={sector1 as any}>
          <Layer
            id="Sector1"
            type="line"
            paint={{
              "line-color": ["get", "line-color"],
              "line-opacity": ["get", "line-opacity"],
              "line-width": ["get", "line-width"],
            }}
          />
        </Source>
        <Source id="Sector2" type="geojson" data={sector2 as any}>
          <Layer
            id="Sector2"
            type="line"
            paint={{
              "line-color": ["get", "line-color"],
              "line-opacity": ["get", "line-opacity"],
              "line-width": ["get", "line-width"],
            }}
          />
        </Source>{" "}
        <Source id="Sector3" type="geojson" data={sector3 as any}>
          <Layer
            id="Sector3"
            type="line"
            paint={{
              "line-color": ["get", "line-color"],
              "line-opacity": ["get", "line-opacity"],
              "line-width": ["get", "line-width"],
            }}
          />
        </Source>
        {markerLocationState.map((location, index) => {
          return (
            <Marker
              key={String(location.samplingLocation.id) + index}
              style={{ cursor: "pointer", opacity: 0.5 }}
              longitude={location.longitude}
              latitude={location.latitude}
              onClick={(ev) => {
                console.log("marker clicked");
                ev.originalEvent.stopPropagation();
                setShowDatesPopup({
                  display: true,
                  locationId: location.samplingLocation.id,
                  name: location.samplingLocation.name,
                  lat: location.latitude,
                  lon: location.longitude,
                });
                setShowPopup((state) => ({ ...state, display: false }));
              }}
            >
              <img src={location.src} alt="" width="20px" />
            </Marker>
          );
        })}
        {showDatesPopup.display &&
          showDatesPopup.locationId &&
          showDatesPopup.name &&
          showDatesPopup.lat &&
          showDatesPopup.lon && (
            <DatesForLocation
              locationId={showDatesPopup.locationId}
              name={showDatesPopup.name}
              lat={showDatesPopup.lat}
              lon={showDatesPopup.lon}
              setShow={setShowDatesPopup}
            />
          )}
        {markerDataState.map((data, index) => {
          return (
            <Marker
              key={String(data.samplingLocation.id) + index}
              style={{ cursor: "pointer", zIndex: 1 }}
              longitude={data.longitude}
              latitude={data.latitude}
              onClick={(ev) => {
                ev.originalEvent.stopPropagation();
                setShowPopup({
                  display: true,
                  location: data.samplingLocation,
                  data: data.samplingData,
                });
                setShowDatesPopup({
                  display: false,
                  locationId: undefined,
                  name: undefined,
                  lat: undefined,
                  lon: undefined,
                });
                dispatcher(
                  detailViewSlice.actions.setSelectedLocation(
                    data.samplingLocation
                  )
                );
              }}
            >
              <img src={data.src} alt="" width="20px" />
            </Marker>
          );
        })}
        {showPopup.display && showPopup.location && showPopup.data && (
          <MapboxPopup
            location={showPopup.location}
            samplingData={showPopup.data}
            setShowPopup={setShowPopup}
          />
        )}
      </MapComponent>
    </Box>
  );
};
